import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { 
  Play, 
  Square, 
  Video, 
  Clock, 
  Plus, 
  Activity, 
  Layout, 
  Settings, 
  Upload, 
  Link as LinkIcon,
  RefreshCw,
  Trash2,
  AlertCircle,
  FileText,
  CheckCircle2,
  Repeat
} from 'lucide-react';
import { toast } from 'sonner';

export default function LivestreamManager() {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  
  // New Stream Form
  const [form, setForm] = useState({
    videoPath: '',
    rtmpUrl: 'rtmps://live-api-s.facebook.com:443/rtmp/',
    streamKey: '',
    loop: false,
    loopCount: 1,
    title: '',
    showClock: false,
    watermarkText: '',
    watermarkPosition: 'bottom-right',
    watermarkFontsize: 24,
    scheduleTime: '',
    quality: '720p'
  });
  
  const [uploading, setUploading] = useState(false);
  const [fileInput, setFileInput] = useState(null);

  const [logsOpen, setLogsOpen] = useState(false);
  const [currentLogs, setCurrentLogs] = useState('');
  const [logStreamId, setLogStreamId] = useState(null);
  
  const [deleteId, setDeleteId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    fetchStreams();
    const interval = setInterval(fetchStreams, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStreams = async () => {
    try {
      const res = await adminAPI.getStreams();
      setStreams(res.data);
    } catch (error) {
      console.error("Failed to fetch streams", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async (id) => {
    try {
      const res = await adminAPI.getStreamLogs(id);
      setCurrentLogs(res.data.logs);
      setLogStreamId(id);
      setLogsOpen(true);
    } catch (error) {
      toast.error('Không thể tải log');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const res = await adminAPI.uploadStreamVideo(formData);
      setForm(prev => ({ ...prev, videoPath: res.data.path }));
      toast.success('Video uploaded successfully!');
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleStartStream = async () => {
    if (!form.videoPath || !form.streamKey) {
      toast.error('Vui lòng chọn video và nhập Stream Key');
      return;
    }

    setStarting(true);
    try {
      const requestData = {
        video_path: form.videoPath,
        rtmp_url: form.rtmpUrl,
        stream_key: form.streamKey,
        loop: form.loop,
        loop_count: form.loop ? -1 : form.loopCount,
        title: form.title,
        show_clock: form.showClock,
        watermark_text: form.watermarkText || null,
        watermark_position: form.watermarkPosition,
        watermark_fontsize: form.watermarkFontsize,
        schedule_time: form.scheduleTime || null,
        quality: form.quality
      };

      const res = await adminAPI.startStream(requestData);
      if (res.data.success) {
        toast.success(res.data.message);
        fetchStreams();
      }
    } catch (error) {
      toast.error('Lỗi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setStarting(false);
    }
  };

  const handleStopStream = async (id) => {
    try {
      await adminAPI.stopStream(id);
      toast.success('Đã dừng stream');
      fetchStreams();
    } catch (error) {
      toast.error('Lỗi khi dừng: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteStream = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await adminAPI.deleteStream(deleteId);
      toast.success('Đã xóa luồng');
      setConfirmDeleteOpen(false);
      setDeleteId(null);
      fetchStreams();
    } catch (error) {
      toast.error('Lỗi khi xóa');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stream Status Column */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-none shadow-xl shadow-blue-50/50 bg-white">
            <CardHeader className="border-b border-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-[#1877F2]">
                    <Activity size={18} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Danh sách Livestream</CardTitle>
                    <CardDescription>Trạng thái các luồng đang phát hoặc đã phát</CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={fetchStreams}>
                  <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {streams.length === 0 ? (
                <div className="p-12 text-center text-[#606770]">
                  <Video size={48} className="mx-auto mb-4 opacity-10" />
                  <p>Chưa có luồng livestream nào được tạo</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {streams.map((s) => (
                    <div key={s.id} className="p-5 flex items-center justify-between hover:bg-gray-50/50 transition-all">
                      <div className="flex gap-4 min-w-0 flex-1">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${s.status === 'streaming' ? 'bg-green-50 text-green-500 animate-pulse' : (s.status === 'crashed' ? 'bg-red-50 text-red-500' : 'bg-gray-50 text-gray-400')}`}>
                          <Video size={24} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-[#1C1E21] truncate">{s.title || "Không có tiêu đề"}</h4>
                            {s.status === 'streaming' ? (
                              <div className="flex gap-1">
                                <Badge className="bg-green-500 hover:bg-green-600 border-none font-bold text-[10px]">LIVE</Badge>
                                <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600 font-bold bg-blue-50 uppercase tracking-tighter">
                                    {s.quality === '1080p' ? 'Full HD 1080p' : 'HD 720p'}
                                </Badge>
                              </div>
                            ) : (
                              <Badge variant={s.status === 'crashed' ? 'destructive' : 'secondary'} className="text-[10px] uppercase">{s.status}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-[#606770] truncate max-w-md">{s.video_path}</p>
                          <div className="flex items-center gap-3 mt-2 text-[10px] font-bold text-[#8C939D] uppercase tracking-wider">
                            <span className="flex items-center gap-1"><Clock size={10} /> {new Date(s.start_time).toLocaleString()}</span>
                            {s.status === 'streaming' && (
                                <>
                                    <span className={s.stats?.bitrate && s.stats.bitrate !== '0.0kbits/s' ? "text-blue-500 flex items-center gap-1" : "text-amber-500 flex items-center gap-1 animate-pulse"}>
                                        <Activity size={10} /> {s.stats?.bitrate ? (s.stats.bitrate === '0.0kbits/s' ? 'ĐANG KẾT NỐI...' : s.stats.bitrate) : 'ĐANG KHỞI TẠO...'}
                                    </span>
                                    {s.stats?.fps && <span className="text-indigo-500 flex items-center gap-1"><Activity size={10} /> {s.stats.fps} FPS</span>}
                                    {s.stats?.loop_count && <span className="text-pink-500 flex items-center gap-1"><Repeat size={10} /> Lặp: {s.stats.loop_count}</span>}
                                    {s.stats?.time && <span className="text-emerald-500 font-mono tracking-tighter bg-emerald-50/50 px-1 rounded">T: {s.stats.time}</span>}
                                </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-4">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-9 px-3 text-xs font-bold gap-1 text-[#1877F2] hover:bg-blue-50 rounded-xl"
                            onClick={() => handleViewLogs(s.id)}
                        >
                            <FileText size={14} /> Log
                        </Button>
                        {s.status === 'streaming' ? (
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="h-9 px-4 rounded-xl font-bold gap-2"
                            onClick={() => handleStopStream(s.id)}
                          >
                            <Square size={14} /> Dừng
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleDeleteStream(s.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Configuration Column */}
        <div className="space-y-6">
          <Card className="border-none shadow-xl shadow-blue-50/50 bg-white">
            <CardHeader className="border-b border-gray-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-lg">
                  <Settings size={18} />
                </div>
                <CardTitle className="text-lg">Cấu hình luồng mới</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#606770]">Video Nguồn</Label>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Nhập link video hoặc path" 
                    value={form.videoPath}
                    onChange={(e) => setForm({...form, videoPath: e.target.value})}
                    className="flex-1 rounded-xl h-11 border-gray-100 bg-gray-50/50 text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="h-11 w-11 shrink-0 rounded-xl border-gray-100 hover:bg-gray-50"
                    onClick={() => document.getElementById('video-upload').click()}
                    disabled={uploading}
                  >
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                  </Button>
                  <input 
                    type="file" 
                    id="video-upload" 
                    className="hidden" 
                    accept="video/*"
                    onChange={handleFileUpload}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#606770]">Facebook RTMP URL</Label>
                <Input 
                  value={form.rtmpUrl}
                  onChange={(e) => setForm({...form, rtmpUrl: e.target.value})}
                  className="rounded-xl h-11 border-gray-100 bg-gray-50/50 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#606770]">Stream Key (FB)</Label>
                <Input 
                  placeholder="FB-XXXXXXXX-X-XXXXXX" 
                  value={form.streamKey}
                  onChange={(e) => setForm({...form, streamKey: e.target.value})}
                  className="rounded-xl h-11 border-gray-100 bg-gray-50/50 text-sm font-mono"
                />
              </div>

              <div className="pt-2 border-t border-gray-50 pb-2">
                <h5 className="text-[10px] font-black tracking-[0.2em] text-[#8C939D] uppercase mb-4">Tùy chọn hiển thị</h5>
                
                <div className="space-y-4">
                   <div className="space-y-2">
                     <Label className="text-xs font-bold">Tên luồng (Phân biệt)</Label>
                     <Input 
                        placeholder="VD: Page Huyen Anh - Live 1" 
                        value={form.title}
                        onChange={(e) => setForm({...form, title: e.target.value})}
                        className="rounded-lg h-9 text-xs border-gray-100"
                     />
                   </div>

                   <div className="flex items-center justify-between">
                     <div className="space-y-0.5">
                       <Label className="text-xs font-bold">Lặp vô tận (Infinite Loop)</Label>
                       <p className="text-[10px] text-[#606770]">Tự động phát lại không giới hạn</p>
                     </div>
                     <Switch checked={form.loop} onCheckedChange={(val) => setForm({...form, loop: val, loopCount: val ? 1 : form.loopCount})} />
                   </div>

                   {!form.loop && (
                     <div className="space-y-2 pt-2 border-t border-dashed border-gray-100 animate-fadeIn">
                       <div className="flex items-center justify-between">
                         <Label className="text-xs font-bold text-[#606770]">Số lần lặp lại (Số lần phát)</Label>
                         <span className="text-[10px] font-mono font-bold text-[#1877F2] bg-blue-50 px-2 py-0.5 rounded">
                           {form.loopCount} lần
                         </span>
                       </div>
                       <Input 
                         type="number"
                         min="1"
                         max="100"
                         value={form.loopCount}
                         onChange={(e) => setForm({...form, loopCount: Math.max(1, parseInt(e.target.value) || 1)})}
                         className="rounded-lg h-9 text-xs border-gray-100 bg-gray-50/50"
                       />
                       <p className="text-[9px] text-[#8C939D]">Số lần video nguồn phát hết toàn bộ nội dung (Mặc định: 1 - phát 1 lần)</p>
                     </div>
                   )}

                   <div className="flex items-center justify-between">
                     <div className="space-y-0.5">
                       <Label className="text-xs font-bold">Đồng hồ hệ thống</Label>
                       <p className="text-[10px] text-[#606770]">Hiển thị giờ trên góc phải</p>
                     </div>
                     <Switch checked={form.showClock} onCheckedChange={(val) => setForm({...form, showClock: val})} />
                   </div>

                   {/* Watermark Customization */}
                   <div className="space-y-3 pt-3 border-t border-dashed border-gray-100">
                     <div className="space-y-2">
                       <Label className="text-xs font-bold">Chữ Watermark</Label>
                       <Input 
                         placeholder="VD: Hotface Live" 
                         value={form.watermarkText}
                         onChange={(e) => setForm({...form, watermarkText: e.target.value})}
                         className="rounded-lg h-9 text-xs border-gray-100"
                       />
                     </div>

                     <div className="space-y-2">
                       <Label className="text-xs font-bold">Vị trí Watermark</Label>
                       <div className="grid grid-cols-2 gap-2">
                         {[
                           { value: 'top-left', label: '↖ Trên Trái' },
                           { value: 'top-right', label: '↗ Trên Phải' },
                           { value: 'bottom-left', label: '↙ Dưới Trái' },
                           { value: 'bottom-right', label: '↘ Dưới Phải' },
                         ].map(pos => (
                           <button
                             key={pos.value}
                             className={`py-1.5 px-2 rounded-lg border text-[10px] font-bold transition-all ${
                               form.watermarkPosition === pos.value 
                                 ? 'border-[#1877F2] bg-blue-50 text-[#1877F2]' 
                                 : 'border-gray-100 text-gray-400 hover:border-gray-200'
                             }`}
                             onClick={() => setForm({...form, watermarkPosition: pos.value})}
                           >
                             {pos.label}
                           </button>
                         ))}
                       </div>
                     </div>

                     <div className="space-y-2">
                       <div className="flex items-center justify-between">
                         <Label className="text-xs font-bold">Cỡ chữ</Label>
                         <span className="text-[10px] font-mono font-bold text-[#1877F2] bg-blue-50 px-2 py-0.5 rounded">{form.watermarkFontsize}px</span>
                       </div>
                       <input 
                         type="range" 
                         min="12" 
                         max="72" 
                         step="2"
                         value={form.watermarkFontsize}
                         onChange={(e) => setForm({...form, watermarkFontsize: parseInt(e.target.value)})}
                         className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#1877F2]"
                       />
                       <div className="flex justify-between text-[9px] text-gray-300 font-bold">
                         <span>12px</span>
                         <span>72px</span>
                       </div>
                     </div>
                   </div>

                </div>
              </div>

              <div className="pt-2 border-t border-gray-50 pb-2">
                <h5 className="text-[10px] font-black tracking-[0.2em] text-[#8C939D] uppercase mb-4">Chất lượng hình ảnh</h5>
                <div className="grid grid-cols-2 gap-3">
                    <button 
                        className={`py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${form.quality === '720p' ? 'border-[#1877F2] bg-blue-50 text-[#1877F2]' : 'border-gray-100 text-gray-400 opacity-60'}`}
                        onClick={() => setForm({...form, quality: '720p'})}
                    >
                        HD 720p
                    </button>
                    <button 
                        className={`py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${form.quality === '1080p' ? 'border-[#1877F2] bg-blue-50 text-[#1877F2]' : 'border-gray-100 text-gray-400 opacity-60'}`}
                        onClick={() => setForm({...form, quality: '1080p'})}
                    >
                        Full HD 1080p
                    </button>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-50 pb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#606770] mb-2 block">Thời gian phát</Label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <button 
                        type="button"
                        className={`py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${!form.scheduleTime ? 'border-[#1877F2] bg-blue-50 text-[#1877F2]' : 'border-gray-100 text-gray-400 opacity-60 hover:opacity-100'}`}
                        onClick={() => setForm({...form, scheduleTime: ''})}
                    >
                        Đăng ngay
                    </button>
                    <button 
                        type="button"
                        className={`py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all ${form.scheduleTime ? 'border-[#1877F2] bg-blue-50 text-[#1877F2]' : 'border-gray-100 text-gray-400 opacity-60 hover:opacity-100'}`}
                        onClick={() => {
                          const now = new Date();
                          now.setMinutes(now.getMinutes() + 10);
                          const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                          setForm({...form, scheduleTime: localIso});
                        }}
                    >
                        Lên lịch phát
                    </button>
                </div>

                {form.scheduleTime && (
                  <div className="flex items-center gap-2 mt-2 p-3 bg-blue-50/50 rounded-xl border border-blue-100 animate-fadeIn">
                    <Clock size={16} className="text-[#1877F2]" />
                    <Input 
                      type="datetime-local" 
                      className="bg-transparent border-none p-0 h-auto text-xs font-bold text-[#1877F2] focus:ring-0 w-full"
                      value={form.scheduleTime}
                      onChange={(e) => setForm({...form, scheduleTime: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <Button 
                className="w-full h-12 rounded-2xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-bold text-base shadow-lg shadow-blue-100 gap-2 transition-all hover:scale-[1.02]"
                onClick={handleStartStream}
                disabled={starting}
              >
                {starting ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
                {form.scheduleTime ? 'Đặt lịch Phát' : 'Bắt đầu Stream'}
              </Button>
              <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100 text-[10px] text-emerald-700 leading-relaxed italic">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                <span>Hệ thống sử dụng FFmpeg tích hợp sẵn, đảm bảo ổn định khi phát 1080p.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
                <FileText className="text-blue-500" />
                Chi tiết Log luồng: {logStreamId}
            </DialogTitle>
            <DialogDescription>100 dòng log cuối cùng từ FFmpeg</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-slate-900 rounded-xl p-4 mt-4 font-mono text-[10px] text-green-400 whitespace-pre">
            {currentLogs || "Đang tải log..."}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsOpen(false)} className="rounded-xl">Đóng</Button>
            <Button variant="secondary" onClick={() => handleViewLogs(logStreamId)} className="rounded-xl gap-2">
                <RefreshCw size={14} /> Cập nhật
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-md">
           <DialogHeader>
             <DialogTitle className="flex items-center gap-2 text-red-600">
               <Trash2 size={20} />
               Xác nhận xóa luồng
             </DialogTitle>
             <DialogDescription className="pt-2">
               Bạn có chắc chắn muốn xóa luồng này khỏi danh sách lịch sử không? 
               Hành động này không thể hoàn tác.
             </DialogDescription>
           </DialogHeader>
           <DialogFooter className="gap-2">
             <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} className="rounded-xl">Hủy bỏ</Button>
             <Button variant="destructive" onClick={confirmDelete} className="rounded-xl px-6">Xác nhận Xóa</Button>
           </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Loader2({size, className}) {
  return <RefreshCw size={size} className={className} />;
}
